import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Button } from './Button';

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: { qty: number; notes?: string }) => void;
  initial?: { qty: number; notes?: string };
}

export function AddToOrderSheet({ open, onClose, onSubmit, initial }: Props) {
  const { t } = useTranslation();
  const [qty, setQty] = useState(initial?.qty ?? 1);
  const [notes, setNotes] = useState(initial?.notes ?? '');

  useEffect(() => {
    if (open) {
      setQty(initial?.qty ?? 1);
      setNotes(initial?.notes ?? '');
    }
  }, [open, initial?.qty, initial?.notes]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  return (
    <AnimatePresence>
      {open ? (
        <>
          <motion.div
            className="fixed inset-0 bg-oud-900/50 z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            className="fixed inset-x-0 bottom-0 z-50 bg-sand rounded-t-3xl p-6 pb-8 shadow-quote border-t border-oud-100 max-w-md mx-auto"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 320, damping: 34 }}
          >
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-black text-oud-900">{t('add_sheet.title')}</h3>
              <button
                type="button"
                onClick={onClose}
                aria-label={t('add_sheet.cancel')}
                className="w-9 h-9 rounded-full bg-cream border border-oud-100 text-oud-700 hover:text-oud-900 flex items-center justify-center"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" className="w-4 h-4" aria-hidden>
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
              </button>
            </div>

            <div className="space-y-5">
              <div>
                <label className="block text-sm font-bold text-oud-800 mb-2">
                  {t('add_sheet.quantity')}
                </label>
                <div className="flex items-center gap-3">
                  <QtyButton
                    onClick={() => setQty((q) => Math.max(1, q - 1))}
                    aria-label="−"
                  >
                    −
                  </QtyButton>
                  <div className="flex-1 text-center text-2xl font-black text-oud-900 tabular-nums">
                    {qty}
                  </div>
                  <QtyButton onClick={() => setQty((q) => Math.min(99, q + 1))} aria-label="+">
                    +
                  </QtyButton>
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-oud-800 mb-2">
                  {t('add_sheet.notes')}
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={t('add_sheet.notes_placeholder')}
                  rows={3}
                  className="w-full bg-cream border border-oud-100/60 rounded-2xl px-4 py-3 text-oud-900 text-sm resize-none focus:outline-none focus:border-gold"
                />
              </div>

              <Button
                size="lg"
                fullWidth
                onClick={() => {
                  onSubmit({ qty, notes: notes.trim() || undefined });
                  onClose();
                }}
              >
                {t('add_sheet.add')}
              </Button>
            </div>
          </motion.div>
        </>
      ) : null}
    </AnimatePresence>
  );
}

function QtyButton({
  children,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      className="w-12 h-12 rounded-full bg-oud-800 text-sand text-2xl font-black flex items-center justify-center active:scale-95 transition-transform"
      {...rest}
    >
      {children}
    </button>
  );
}
